import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listItemUoms, previewStockingQuantity } from '../items/api';
import {
  postAdjustment,
  scanItem,
  scanLocation,
  type ScannedItemDto,
  type ScannedLocationDto,
} from './api';

/** Legacy's Location column is varchar(15) and its Reference varchar(20). */
const LOCATION_MAX = 15;
const REFERENCE_MAX = 20;

/**
 * Both codes are legacy's own, seeded by tenant script 035 in the `Adj` group: RC1 "Non-PO Receipt"
 * adds stock, RC2 "Non-PO Receipt Reversal" removes it. `sp_ITMobile_ProductionReceipt` picks
 * between them on its `@IsReversal` flag and this screen does the same.
 *
 * No PROD code is invented for this. Transaction codes are a tenant-maintained master seeded from a
 * real extract, and adding one here would put a code in the seed that no legacy tenant has and that
 * their reports do not know — the standing rule is not to invent reference data. The cost of
 * reusing RC1 is that legacy cannot tell produced goods from any other non-PO receipt in the
 * ledger; `referenceType` below closes that without touching the code list.
 */
const RECEIPT_CODE = 'RC1';
const REVERSAL_CODE = 'RC2';

/**
 * Stamped on every posting from this screen, which is what makes it worth having as its own screen
 * rather than a note in the adjustment reason. Legacy posts production through RC1 with nothing to
 * mark it, so "what did we make last week" is unanswerable there: RC1 covers production, found
 * stock, customer returns and anything else somebody receives without a PO. The ledger read and
 * the inventory screen already display this, so filtering to production needs no further work.
 */
const REFERENCE_TYPE = 'production';

/**
 * Production Receipt — finished goods into stock.
 *
 * Its own screen because that is what the current product does: a dedicated handheld action, so an
 * operator coming off a line scans an item, scans where it went, and is done. The adjustment screen
 * can already post exactly this — pick "Non-PO Receipt" from the transaction code list — but making
 * somebody choose the right code out of a list of twenty, every time, on a device with a four-inch
 * screen, is how the wrong code gets picked.
 *
 * WHAT THE CURRENT PRODUCT DOES THAT THIS DELIBERATELY DOES NOT
 * -------------------------------------------------------------
 * `sp_ITMobile_ProductionReceipt` hard-codes `@UM = 'CASE', @Quantity = 1` — one case per scan, and
 * it REFUSES the receipt with error 800 when the item has no CASE conversion. So an item stocked in
 * eaches with no case defined cannot be production-received at all there. Quantity and unit are
 * therefore fields here, defaulted to 1 and the item's stocking unit, which is the same behaviour
 * for a case-stocked item and simply works for everything else.
 *
 * It also always posts an EMPTY serial number, so a serialized item receives without its serial. If
 * the item is serialized this screen asks, because the server can take it.
 *
 * AND THE ONE STEP THAT DISAPPEARS
 * --------------------------------
 * Legacy needs a second procedure, `sp_ITMobile_ProductionLoadLP`, to get produced stock onto a
 * pallet: it posts a two-legged T1 transfer out of one plate and into another, because its receipt
 * cannot name a plate. Ours can — scan the pallet into the location field and the receipt lands on
 * it directly. One posting instead of three, and no transfer in the ledger that never really
 * happened.
 *
 * Starting a NEW plate is not offered here; that is the adjustment screen's job. Produced goods go
 * onto a plate that has already been printed, and prompting an operator to invent a pallet mid-flow
 * is how a typo becomes a pallet.
 */
export function ProductionReceiptRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const warehouseId = useCurrentWarehouseId();

  const [isReversal, setIsReversal] = useState(false);

  const [itemText, setItemText] = useState('');
  const [resolvedItem, setResolvedItem] = useState<ScannedItemDto | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);
  const [scanningItem, setScanningItem] = useState(false);

  const [locationText, setLocationText] = useState('');
  const [resolvedLocation, setResolvedLocation] = useState<ScannedLocationDto | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [scanningLocation, setScanningLocation] = useState(false);

  const [quantity, setQuantity] = useState<number | string>(1);
  const [uomId, setUomId] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [reference, setReference] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const itemRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);

  const itemUoms = useQuery({
    queryKey: ['item-uoms', resolvedItem?.itemId ?? 'none'] as const,
    queryFn: ({ signal }) => listItemUoms(resolvedItem!.itemId, signal),
    enabled: Boolean(resolvedItem?.itemId),
  });

  const uomOptions = useMemo(
    () =>
      (itemUoms.data ?? []).map((u) => ({
        value: u.uomId,
        label: `${u.uomCode} — ${u.displayName}`,
      })),
    [itemUoms.data],
  );

  const stockingUom = useMemo(
    () => (itemUoms.data ?? []).find((u) => u.isStockingUom) ?? null,
    [itemUoms.data],
  );
  const selectedUom = useMemo(
    () => (itemUoms.data ?? []).find((u) => u.uomId === uomId) ?? null,
    [itemUoms.data, uomId],
  );

  // Advisory only — the server does the real conversion. Shown for the same reason the adjustment
  // screen shows it: an operator entering 24 EA of a case-stocked item should see "= 2 CS" rather
  // than find out from a balance later.
  const preview = useMemo(() => {
    if (!selectedUom || selectedUom.isStockingUom || quantity === '') return null;
    const converted = previewStockingQuantity(Number(quantity), selectedUom.factor);
    if (converted === null || !stockingUom) return null;
    return { converted, code: stockingUom.uomCode };
  }, [selectedUom, stockingUom, quantity]);

  // ---- Scans --------------------------------------------------------------

  const runItemScan = useCallback(
    async (advanceTo?: HTMLInputElement | null) => {
      const number = itemText.trim();
      if (!number) {
        setResolvedItem(null);
        setItemError(null);
        return;
      }
      if (resolvedItem && resolvedItem.itemNumber === number.toUpperCase()) return;

      setScanningItem(true);
      setItemError(null);
      try {
        const found = await scanItem(number);
        setResolvedItem(found);
        setItemText(found.itemNumber);
        // The stocking unit is what production is counted in far more often than not, and it is the
        // one unit every item has. Legacy's fixed CASE is not a default so much as a requirement.
        setUomId(found.primaryUomId);
        advanceTo?.focus();
      } catch (err) {
        // A mis-scan costs one beep and the focus comes back, rather than surviving to the post.
        setResolvedItem(null);
        setItemText('');
        setUomId('');
        setItemError(
          err instanceof ApiError
            ? (err.problem.detail ?? err.problem.title ?? t('inventory.productionScreen.itemUnknown'))
            : t('inventory.productionScreen.itemUnknown'),
        );
        itemRef.current?.focus();
      } finally {
        setScanningItem(false);
      }
    },
    [itemText, resolvedItem, t],
  );

  const runLocationScan = useCallback(
    async (advanceTo?: HTMLInputElement | null) => {
      const code = locationText.trim();
      if (!code) {
        setResolvedLocation(null);
        setLocationError(null);
        return;
      }
      if (resolvedLocation && resolvedLocation.locationCode === code.toUpperCase()) return;

      setScanningLocation(true);
      setLocationError(null);
      try {
        const found = await scanLocation(code, warehouseId || undefined);
        setResolvedLocation(found);
        setLocationText(found.licensePlateNumber ?? found.locationCode);
        advanceTo?.focus();
      } catch (err) {
        setResolvedLocation(null);
        setLocationText('');
        setLocationError(
          err instanceof ApiError
            ? (err.problem.detail ??
              err.problem.title ??
              t('inventory.productionScreen.locationUnknown'))
            : t('inventory.productionScreen.locationUnknown'),
        );
        locationRef.current?.focus();
      } finally {
        setScanningLocation(false);
      }
    },
    [locationText, resolvedLocation, warehouseId, t],
  );

  // ---- Reset --------------------------------------------------------------

  /**
   * Keeps the LOCATION and the reversal switch, clears everything else. Somebody receiving a run
   * off a line posts item after item into the same place, so re-scanning the location every time
   * would be the slowest part of the job. The adjustment screen makes the same choice.
   */
  const resetForNext = useCallback(() => {
    setItemText('');
    setResolvedItem(null);
    setItemError(null);
    setQuantity(1);
    setUomId('');
    setLotNumber('');
    setSerialNumber('');
    setSubmitError(null);
    itemRef.current?.focus();
  }, []);

  const resetScreen = useCallback(() => {
    resetForNext();
    setLocationText('');
    setResolvedLocation(null);
    setLocationError(null);
    setReference('');
  }, [resetForNext]);

  // ---- Post ---------------------------------------------------------------

  const needsLot = resolvedItem?.isLotControlled ?? false;
  const needsSerial = resolvedItem?.isSerialized ?? false;

  const canPost =
    Boolean(resolvedItem) &&
    Boolean(resolvedLocation) &&
    Boolean(uomId) &&
    quantity !== '' &&
    Number(quantity) > 0 &&
    (!needsLot || lotNumber.trim().length > 0) &&
    (!needsSerial || serialNumber.trim().length > 0);

  const mutation = useMutation({
    mutationFn: () =>
      postAdjustment({
        warehouseId: resolvedLocation!.warehouseId,
        itemId: resolvedItem!.itemId,
        locationId: resolvedLocation!.locationId,
        // A positive magnitude either way. Which direction stock moves is the transaction code's
        // business, so a sign here as well would only give the two a way to disagree.
        quantity: Math.abs(Number(quantity)),
        uomId,
        tranCode: isReversal ? REVERSAL_CODE : RECEIPT_CODE,
        // Scanned a pallet rather than a shelf: the receipt lands on the pallet. No balance type —
        // the server takes it from the location, which is where the stock category lives.
        licensePlateId: resolvedLocation!.licensePlateId,
        lotNumber: needsLot ? lotNumber.trim().toUpperCase() : null,
        serialNumber: needsSerial ? serialNumber.trim().toUpperCase() : null,
        referenceType: REFERENCE_TYPE,
        referenceId: reference.trim() || null,
      }),
    onSuccess: () => {
      notifications.show({
        message: t(
          isReversal
            ? 'inventory.productionScreen.reversed'
            : 'inventory.productionScreen.posted',
          {
            quantity: Number(quantity).toLocaleString(),
            item: resolvedItem?.itemNumber ?? '',
          },
        ),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      resetForNext();
    },
    onError: (err) => {
      if (!(err instanceof ApiError)) {
        setSubmitError(t('inventory.productionScreen.failed'));
        return;
      }
      const type = err.problem.type ?? '';
      // The server names the item and both quantities in these, so its own detail is better text
      // than anything this screen could compose.
      if (
        type.includes('lot_needs_expiration') ||
        type.includes('would_go_negative') ||
        type.includes('insufficient_at_location') ||
        type.includes('serial_already_on_hand')
      ) {
        setSubmitError(err.problem.detail ?? err.problem.title ?? t('inventory.productionScreen.failed'));
        return;
      }
      setSubmitError(err.problem.detail ?? err.problem.title ?? t('inventory.productionScreen.failed'));
    },
  });

  if (!permissions.has('inventory:adjust')) {
    return (
      <Stack gap="md">
        <PageHeader title={t('inventory.productionScreen.title')} />
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={18} />}>
          {t('inventory.productionScreen.notPermitted')}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader title={t('inventory.productionScreen.title')} />

      <Card withBorder padding="md">
        <Stack gap="md">
          <Switch
            checked={isReversal}
            onChange={(e) => setIsReversal(e.currentTarget.checked)}
            label={t('inventory.productionScreen.reversal')}
            description={t('inventory.productionScreen.reversalHint')}
          />

          {/* One column on a handheld, two from tablet up. The field order is the current
              product's, so muscle memory carries over. */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              ref={itemRef}
              label={t('inventory.productionScreen.itemNumber')}
              description={t('inventory.productionScreen.itemHint')}
              value={itemText}
              onChange={(e) => setItemText(e.currentTarget.value)}
              onBlur={() => void runItemScan()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void runItemScan(locationRef.current);
                }
              }}
              error={itemError}
              disabled={scanningItem}
              autoFocus
              data-autofocus
            />

            <TextInput
              ref={locationRef}
              label={t('inventory.productionScreen.location')}
              description={t('inventory.productionScreen.locationHint')}
              placeholder={t('inventory.productionScreen.locationPlaceholder')}
              value={locationText}
              maxLength={LOCATION_MAX}
              onChange={(e) => setLocationText(e.currentTarget.value)}
              onBlur={() => void runLocationScan()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void runLocationScan(quantityRef.current);
                }
              }}
              error={locationError}
              disabled={scanningLocation}
            />
          </SimpleGrid>

          {resolvedItem && (
            <Text size="sm" c="dimmed">
              {resolvedItem.itemNumber} — {resolvedItem.description}
            </Text>
          )}
          {resolvedLocation && (
            <Text size="sm" c="dimmed">
              {resolvedLocation.kind === 'license_plate'
                ? t('inventory.productionScreen.resolvedPlate', {
                    plate: resolvedLocation.licensePlateNumber ?? '',
                    location: resolvedLocation.locationCode,
                  })
                : t('inventory.productionScreen.resolvedLocation', {
                    location: resolvedLocation.locationCode,
                    balanceType: resolvedLocation.balanceTypeName,
                  })}
            </Text>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput
              ref={quantityRef}
              label={t('inventory.productionScreen.quantity')}
              description={t('inventory.productionScreen.quantityHint')}
              value={quantity}
              onChange={setQuantity}
              min={0}
              decimalScale={4}
              step={1}
            />
            <Select
              label={t('inventory.productionScreen.um')}
              description={t('inventory.productionScreen.umHint')}
              data={uomOptions}
              value={uomId || null}
              onChange={(v) => setUomId(v ?? '')}
              disabled={!resolvedItem || itemUoms.isLoading}
              searchable
              allowDeselect={false}
            />
          </SimpleGrid>

          {preview && (
            <Text size="sm" c="dimmed">
              {t('inventory.uomPreview', {
                quantity: preview.converted.toLocaleString(),
                uom: preview.code,
              })}
            </Text>
          )}

          {(needsLot || needsSerial) && (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {needsLot && (
                <TextInput
                  label={t('inventory.productionScreen.lotNumber')}
                  description={t('inventory.productionScreen.lotHint')}
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.currentTarget.value)}
                />
              )}
              {needsSerial && (
                <TextInput
                  label={t('inventory.productionScreen.serialNumber')}
                  description={t('inventory.productionScreen.serialHint')}
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.currentTarget.value)}
                />
              )}
            </SimpleGrid>
          )}

          <TextInput
            label={t('inventory.productionScreen.reference')}
            description={t('inventory.productionScreen.referenceHint')}
            value={reference}
            maxLength={REFERENCE_MAX}
            onChange={(e) => setReference(e.currentTarget.value)}
          />

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
              {submitError}
            </Alert>
          )}

          {/* Save / Reset / Done, in the current product's order. */}
          <Group>
            <Button
              color="invetrak"
              onClick={() => {
                setSubmitError(null);
                mutation.mutate();
              }}
              disabled={!canPost}
              loading={mutation.isPending}
            >
              {t('inventory.productionScreen.save')}
            </Button>
            <Button variant="default" onClick={resetScreen}>
              {t('inventory.productionScreen.reset')}
            </Button>
            <Button variant="subtle" onClick={() => void navigate({ to: '/app/inventory' })}>
              {t('inventory.productionScreen.done')}
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
